import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { useMappingsState } from '../providers/BulkConfigureContextProvider.tsx';
import type { CreateIssueResponse } from '../__types__/apiUtils.ts';
import { messages } from '../messages.ts';
import {
	useBulkCreateContext,
	useResetSingleIssuesCreated,
} from '../providers/BulkCreateContextProvider.tsx';
import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload.ts';
import type { InsertMultipleStorageFragmentsRequestPayload } from '../__types__/InsertMultipleStorageFragmentsRequestPayload.ts';
import type {
	CellModification,
	InsertStorageColumnTableRequestPayload,
} from '../__types__/InsertStorageColumnTableRequestPayload.ts';
import { useIssueCreateSidePanelContext } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents.tsx';

import { useBulkCreateFlags } from './useBulkCreateFlags.tsx';
import {
	bulkConstructSmartlinkTags,
	constructSmartlinkTag,
	constructHeading,
	constructJiraIssueUrl,
	constructBreakContainerTag,
	insertContentAtSelectionEnd,
	updateLastFetchTimeIfNotViewMode,
	insertMultipleContentAtSelectionEnd,
	insertWorkItemsIntoTable,
	getSummaryInsertionColumn,
} from './issueInsertionUtils.tsx';

const JIRA_SINGLE_ISSUE_LINK_INSERTION = 'jiraSingleIssueLinkInsertion';
const JIRA_MULTIPLE_ISSUE_LINK_INSERTION = 'jiraMultipleIssueLinkInsertion';

export const useGetIssueInsertionActions = () => {
	const { formatMessage } = useIntl();
	const [
		{
			currentJiraSite,
			insertStorageFragmentRequestPayload,
			singleIssuesCreated,
			isCreatedWithAi,
			isCreatedFromActionItems,
			isCreatedFromTable,
			selectedTableAdf,
		},
	] = useBulkCreateContext();
	const [
		{
			insertMultipleStorageFragmentsRequestPayload,
			insertStorageColumnTableRequestPayload,
			pageMode,
			hasEditPermission,
			editorAPI,
		},
	] = useIssueCreateSidePanelContext();
	const { columnToFieldMappings } = useMappingsState();
	const {
		triggerSingleIssueInsertLinkFailureFlag,
		triggerMultipleIssuesInsertLinkFailureFlag,
		triggerMultipleIssuesCreatedFlag,
		triggerDocumentModifiedSingleIssueInsertLinkFailureFlag,
		triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag,
	} = useBulkCreateFlags();
	const resetSingleIssuesCreated = useResetSingleIssuesCreated();
	const { logRequestFailed, logIssueInsertionFailure } = useBulkCreateLoggingEvents();
	const headingString = formatMessage(
		fg('confluence-issue-terminology-refresh')
			? messages.bulkCreateIssueInsertionHeadingIssueTermRefresh
			: messages.bulkCreateIssueInsertionHeading,
	);
	const currentUrl = useRouteDataRef().current?.href;

	const constructXmlModification = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined,
		) => {
			const smartlinkTags = bulkConstructSmartlinkTags(
				createdIssues.map((issue) => constructJiraIssueUrl(currentJiraSite?.url, issue.key)),
			);
			return insertStorageFragmentRequestPayload?.selectedText !== headingString
				? `${constructHeading(headingString)}<br/>${smartlinkTags}`
				: `<p>${smartlinkTags}</p>`;
		},
		[currentJiraSite?.url, headingString],
	);

	const constructXmlModifications = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			fragments: InsertMultipleStorageFragmentsRequestPayload['fragments'],
		) => {
			if (fg('confluence-create-work-items-from-action-items')) {
				const issuesByRowIndex = new Map(
					createdIssues
						.filter((issue) => issue.rowIndex !== undefined)
						.map((issue) => [String(issue.rowIndex), issue]),
				);

				return fragments
					.filter((fragment) => issuesByRowIndex.has(fragment.rowIndex))
					.map((fragment) => {
						const issue = issuesByRowIndex.get(fragment.rowIndex)!;
						return {
							...fragment,
							xmlModification: constructBreakContainerTag(
								constructSmartlinkTag(constructJiraIssueUrl(currentJiraSite?.url, issue.key)),
							),
						};
					});
			}

			// TO DO: remove when cleaning up confluence-create-work-items-from-action-items
			const createdIssueRowIds = new Set(createdIssues.map((issue) => String(issue.rowIndex)));
			return fragments
				.filter((fragment) => createdIssueRowIds.has(fragment.rowIndex))
				.map((fragment, index) => ({
					...fragment,
					xmlModification: constructBreakContainerTag(
						constructSmartlinkTag(
							constructJiraIssueUrl(currentJiraSite?.url, createdIssues[index].key),
						),
					),
				}));
		},
		[currentJiraSite?.url],
	);

	const constructCellModifications = useCallback(
		(createdIssues: CreateIssueResponse[]): CellModification[] | undefined => {
			return createdIssues
				.filter((issue) => issue.rowIndex !== undefined)
				.map((issue) => {
					const xmlInsertion = constructSmartlinkTag(
						constructJiraIssueUrl(currentJiraSite?.url, issue.key),
					);
					return { rowIndex: Number(issue.rowIndex), xmlInsertion };
				});
		},
		[currentJiraSite?.url],
	);

	const constructAnchorLinkToHeading = useCallback(() => {
		const formattedHeading = headingString.split(' ').join('-');
		const [baseUrl, existingHash] = (currentUrl ?? '').split('#');
		return existingHash ? `${baseUrl}#${formattedHeading}` : `${currentUrl}#${formattedHeading}`;
	}, [currentUrl, headingString]);

	const reloadOnClick = useCallback(() => {
		// eslint-disable-next-line no-restricted-syntax
		window.location.href = constructAnchorLinkToHeading();
		window.location.reload();
	}, [constructAnchorLinkToHeading]);

	const isUserSelectedCreation =
		(isCreatedFromTable || isCreatedFromActionItems) &&
		fg('confluence-create-work-items-from-action-items');

	const handleInsertionError = useCallback(
		(_error: unknown, createdIssues: CreateIssueResponse[]) => {
			const isDocumentChangedError = (_error as Error).message === 'document-changed';

			if (isDocumentChangedError && fg('confluence-bulk-work-create-error-messaging')) {
				if (createdIssues.length === 1) {
					triggerDocumentModifiedSingleIssueInsertLinkFailureFlag({
						issueKey: createdIssues[0].key,
						summary: createdIssues[0].summary,
					});
				} else {
					triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag(createdIssues);
				}
			} else if (createdIssues.length === 1) {
				triggerSingleIssueInsertLinkFailureFlag({
					issueKey: createdIssues[0].key,
					summary: createdIssues[0].summary,
				});
				logRequestFailed({
					requestName: JIRA_SINGLE_ISSUE_LINK_INSERTION,
					errorMessage: (_error as Error).message,
				});
			} else {
				triggerMultipleIssuesInsertLinkFailureFlag(createdIssues);
				logRequestFailed({
					requestName: JIRA_MULTIPLE_ISSUE_LINK_INSERTION,
					errorMessage: (_error as Error).message,
				});
			}

			if (expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
				logIssueInsertionFailure(createdIssues.length, _error);
			}
		},
		[
			logIssueInsertionFailure,
			logRequestFailed,
			triggerDocumentModifiedMultipleIssuesInsertLinkFailureFlag,
			triggerDocumentModifiedSingleIssueInsertLinkFailureFlag,
			triggerMultipleIssuesInsertLinkFailureFlag,
			triggerSingleIssueInsertLinkFailureFlag,
		],
	);

	const handleStorageInsertion = useCallback(
		(
			createdIssues: CreateIssueResponse[],
			payload:
				| InsertStorageFragmentRequestPayload
				| InsertStorageColumnTableRequestPayload
				| InsertMultipleStorageFragmentsRequestPayload,
		) => {
			let showReloadAction = false;
			updateLastFetchTimeIfNotViewMode(payload, pageMode);
			let insertPromise = Promise.resolve(false);
			if (isCreatedWithAi && !isUserSelectedCreation) {
				insertPromise = insertContentAtSelectionEnd(payload as InsertStorageFragmentRequestPayload);
			} else if (isCreatedFromActionItems) {
				insertPromise = insertMultipleContentAtSelectionEnd(
					payload as InsertMultipleStorageFragmentsRequestPayload,
				);
			} else if (isCreatedFromTable) {
				const summaryInsertionColumn = getSummaryInsertionColumn(
					columnToFieldMappings,
					isCreatedWithAi,
				);
				insertPromise = insertWorkItemsIntoTable({
					createdIssues,
					currentJiraSiteUrl: currentJiraSite?.url,
					pageMode,
					selectedTableAdf,
					editorAPI,
					payload: payload as InsertStorageColumnTableRequestPayload,
					summaryInsertionColumn,
				});
			}
			insertPromise
				.then((data) => {
					// There is a case where the API response is OK, but the response data is false.
					// When this happens, the backend has trouble inserting the ticket to the page (likely due to a payload error) and we should show an error toast.
					if (data === false) {
						throw new Error('Failed to insert storage');
					}
				})
				.then(() => {
					showReloadAction = expVal('confluence-bulk-work-create-editor', 'isEnabled', false)
						? pageMode === 'view'
						: true;
					if (!expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
						void resetSingleIssuesCreated();
					}
				})
				.catch((_error) => {
					handleInsertionError(_error, createdIssues);
				})
				.finally(() => {
					if (expVal('confluence-bulk-work-create-editor', 'isEnabled', false)) {
						void resetSingleIssuesCreated();
					}
					triggerMultipleIssuesCreatedFlag(createdIssues, reloadOnClick, showReloadAction);
				});
		},
		[
			isCreatedFromActionItems,
			isCreatedFromTable,
			isCreatedWithAi,
			isUserSelectedCreation,
			pageMode,
			reloadOnClick,
			resetSingleIssuesCreated,
			triggerMultipleIssuesCreatedFlag,
			handleInsertionError,
			currentJiraSite?.url,
			editorAPI,
			columnToFieldMappings,
			selectedTableAdf,
		],
	);

	const addSingleIssuesToPage = useCallback(() => {
		//If the user doesn't have edit permissions on the page, we shouldn't attempt to insert the issues.
		if (singleIssuesCreated.length > 0 && !hasEditPermission) {
			triggerMultipleIssuesCreatedFlag(singleIssuesCreated, reloadOnClick, false);
			void resetSingleIssuesCreated();
		} else if (
			insertStorageFragmentRequestPayload &&
			isCreatedWithAi &&
			singleIssuesCreated.length > 0
		) {
			insertStorageFragmentRequestPayload.xmlModification = constructXmlModification(
				singleIssuesCreated,
				insertStorageFragmentRequestPayload,
			);
			handleStorageInsertion(singleIssuesCreated, insertStorageFragmentRequestPayload);
		} else if (
			insertStorageColumnTableRequestPayload &&
			(!isCreatedWithAi || fg('confluence-create-work-items-from-action-items')) &&
			singleIssuesCreated.length > 0
		) {
			insertStorageColumnTableRequestPayload.cellModifications =
				constructCellModifications(singleIssuesCreated);
			handleStorageInsertion(singleIssuesCreated, insertStorageColumnTableRequestPayload);
		} else if (
			!isCreatedFromTable &&
			isCreatedFromActionItems &&
			singleIssuesCreated.length > 0 &&
			insertMultipleStorageFragmentsRequestPayload &&
			(!isCreatedWithAi || fg('confluence-create-work-items-from-action-items'))
		) {
			handleStorageInsertion(singleIssuesCreated, {
				pageId: insertMultipleStorageFragmentsRequestPayload.pageId,
				lastFetchTime: insertMultipleStorageFragmentsRequestPayload.lastFetchTime,
				fragments: constructXmlModifications(
					singleIssuesCreated,
					insertMultipleStorageFragmentsRequestPayload.fragments,
				),
			});
		}
	}, [
		singleIssuesCreated,
		hasEditPermission,
		insertStorageFragmentRequestPayload,
		isCreatedWithAi,
		insertStorageColumnTableRequestPayload,
		isCreatedFromTable,
		isCreatedFromActionItems,
		insertMultipleStorageFragmentsRequestPayload,
		triggerMultipleIssuesCreatedFlag,
		reloadOnClick,
		resetSingleIssuesCreated,
		constructXmlModification,
		handleStorageInsertion,
		constructCellModifications,
		constructXmlModifications,
	]);

	return {
		constructAnchorLinkToHeading,
		constructXmlModification,
		constructXmlModifications,
		constructCellModifications,
		addSingleIssuesToPage,
	};
};
